import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatError } from "@/lib/api";
import { LOGO } from "@/lib/assets";
import { ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If they came from /admin/login default to admin destination; otherwise /parents.
  const forceAdmin = location.pathname === "/admin/login";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email, password);
      if (forceAdmin) {
        navigate(u.role === "admin" ? "/admin" : "/parents");
      } else {
        navigate(u.role === "admin" ? "/admin" : "/parents");
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-icdh-navy flex items-center justify-center p-6">
      <div className="noise-overlay fixed inset-0 opacity-[0.04] pointer-events-none" />
      <div className="relative w-full max-w-md bg-icdh-white p-10 md:p-12">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src={LOGO} alt="ICDH" className="h-12 w-12 object-contain" />
          <div>
            <p className="font-extrabold tracking-tight text-base leading-tight text-icdh-navy">Institution Chrétienne d'Haïti</p>
            <p className="font-serif italic text-icdh-red text-sm">{t.motto}</p>
          </div>
        </Link>
        <h1 className="font-serif text-3xl tracking-tight text-icdh-navy">{t.login.title}</h1>
        <p className="mt-2 text-icdh-navy/60 text-sm">{t.login.sub}</p>

        <form onSubmit={submit} data-testid="login-form" className="mt-8 space-y-6">
          <input required type="email" data-testid="login-email" placeholder={t.login.email} value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 border-b border-icdh-navy/25 focus:border-icdh-red outline-none bg-transparent text-icdh-navy" />
          <input required type="password" data-testid="login-password" placeholder={t.login.password} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full h-12 border-b border-icdh-navy/25 focus:border-icdh-red outline-none bg-transparent text-icdh-navy" />
          {error && <p data-testid="login-error" className="text-icdh-red text-sm">{error}</p>}
          <button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full h-14 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors duration-300 disabled:opacity-60">
            {loading ? "..." : t.login.submit}
          </button>
        </form>

        <Link to="/" data-testid="login-back" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-icdh-navy/60 hover:text-icdh-red transition-colors">
          <ArrowLeft size={14} />{t.login.back}
        </Link>
        <Link to="/forgot-password" data-testid="login-forgot" className="mt-4 block text-xs uppercase tracking-[0.2em] text-icdh-red hover:text-icdh-navy transition-colors">
          Forgot password?
        </Link>
      </div>
    </div>
  );
}
